/**
 * Evidence dashboard (Task 20).
 * Per-card whitelist fields with value / confidence / source / observed_at.
 * Unsourced fields → unknown. Capped rules → usage vs limit. Read-only.
 */

import { formatCentsDisplay } from '../../domain/recommend/explain';
import type { CapUsageView, EvidenceDashboardCardView, EvidenceFieldView } from './types';

function FieldRow({ field }: { field: EvidenceFieldView }): JSX.Element {
  return (
    <div
      data-testid={`evidence-field-${field.field_name}`}
      className="flex flex-col gap-0.5 border-t border-slate-800 py-2 first:border-t-0"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs uppercase tracking-wide text-slate-500">{field.field_name}</span>
        {field.status === 'known' ? (
          <span className="text-xs text-slate-500">observed · {field.observed_at}</span>
        ) : (
          <span className="text-xs text-slate-500">no evidence</span>
        )}
      </div>
      {field.status === 'known' ? (
        <>
          <div className="text-sm text-slate-100">{field.value_display}</div>
          <div className="text-xs text-slate-400">
            {field.confidence} · {field.source.title} ({field.source.kind})
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-400">unknown</div>
      )}
    </div>
  );
}

function CapRow({ cap }: { cap: CapUsageView }): JSX.Element {
  return (
    <div
      data-testid={`evidence-cap-${cap.reward_rule_id}`}
      className="flex flex-col gap-0.5 border-t border-slate-800 py-2 first:border-t-0"
    >
      <span className="text-xs uppercase tracking-wide text-slate-500">
        Reward cap · {cap.label}
      </span>
      <div className="text-sm text-slate-100">
        {formatCentsDisplay(cap.used_cents)} used of {formatCentsDisplay(cap.cap_limit_cents)}
      </div>
      <div className="text-xs text-slate-400">
        {formatCentsDisplay(cap.remaining_cents)} remaining
      </div>
    </div>
  );
}

function EvidenceCard({ card }: { card: EvidenceDashboardCardView }): JSX.Element {
  return (
    <article
      data-testid="evidence-card"
      data-card-id={card.card_id}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <header className="mb-2">
        <h3 className="text-base font-medium text-slate-100">{card.label}</h3>
      </header>

      <div className="mb-3">
        <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          Known fields
        </h4>
        {card.fields.map((field) => (
          <FieldRow key={field.field_name} field={field} />
        ))}
      </div>

      {card.caps.length > 0 ? (
        <div>
          <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
            Reward caps
          </h4>
          {card.caps.map((cap) => (
            <CapRow key={cap.reward_rule_id} cap={cap} />
          ))}
        </div>
      ) : null}
    </article>
  );
}

export type EvidenceDashboardScreenProps = {
  cards: readonly EvidenceDashboardCardView[];
};

export function EvidenceDashboardScreen({ cards }: EvidenceDashboardScreenProps): JSX.Element {
  return (
    <section data-testid="evidence-dashboard-screen" className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-medium text-slate-100">Evidence dashboard</h2>
        <p className="mt-1 text-sm text-slate-400">
          Read-only view of whitelisted card fields and reward-cap usage. Fields without an evidence
          source are unknown.
        </p>
      </header>

      {cards.length === 0 ? (
        <p className="text-sm text-slate-400">No cards loaded.</p>
      ) : (
        cards.map((card) => <EvidenceCard key={card.card_id} card={card} />)
      )}
    </section>
  );
}
